import { useRef } from "react"

import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"
import { DashboardSortDirection } from "ts/types/AdminDashboard"

// A single letter matches most of the school, so it searches nothing.
const MIN_SEARCH_LENGTH = 2
const toSearch = (value: string | null) => {
  const trimmed = value?.trim() ?? ""
  return trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : ""
}

const DEFAULT_SORT = "total_amount"
const DEFAULT_DIR: DashboardSortDirection = "desc"

type NameSortKey = "first_name" | "last_name"

const isNameSortKey = (key: string): key is NameSortKey =>
  key === "first_name" || key === "last_name"

// The app's name sort: first name up, down, then last name up, down.
const NAME_SORT_CYCLE: [NameSortKey, DashboardSortDirection][] = [
  ["first_name", "asc"],
  ["first_name", "desc"],
  ["last_name", "asc"],
  ["last_name", "desc"],
]

const parsePage = (raw: string | null) => {
  const page = raw && /^\d+$/.test(raw) ? Number(raw) : 1
  return page >= 1 ? page : 1
}

interface IDashboardTableStateOptions<K extends string> {
  prefix: string
  sortKeys: (K | typeof DEFAULT_SORT)[]
  // Valid in the URL's sort but tied to no column, so no header shows as sorted.
  extraSortKeys?: K[]
}

// Search, sort and page of a dashboard table, kept in the URL next to the
// tab's period; defaults stay out of it and every change replaces the entry.
const useDashboardTableState = <K extends string>({
  prefix,
  sortKeys,
  extraSortKeys = [],
}: IDashboardTableStateOptions<K>) => {
  type SortKey = K | typeof DEFAULT_SORT
  const { t } = useTranslation()
  const searchKey = `${prefix}_search`
  const sortParamKey = `${prefix}_sort`
  const dirKey = `${prefix}_dir`
  const pageKey = `${prefix}_page`

  const [searchParams, setSearchParams] = useSearchParams()
  // The search field's debounce keeps its first submit handler, so writes merge
  // into the latest params instead of the ones from that render.
  const latestParams = useRef(searchParams)
  latestParams.current = searchParams

  const search = toSearch(searchParams.get(searchKey))
  const rawSort = searchParams.get(sortParamKey) ?? DEFAULT_SORT
  const rawDir = searchParams.get(dirKey) ?? DEFAULT_DIR
  const sortKey = [...sortKeys, ...extraSortKeys].find(
    (key) => key === rawSort,
  )
  const isValidSort = !!sortKey && (rawDir === "asc" || rawDir === "desc")
  const sortBy: SortKey = isValidSort ? sortKey : DEFAULT_SORT
  const sortDir: DashboardSortDirection = isValidSort ? rawDir : DEFAULT_DIR
  const page = parsePage(searchParams.get(pageKey))

  // Changes several keys in one entry; `null` removes a key.
  const write = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(latestParams.current)
    Object.entries(patch).forEach(([key, value]) =>
      value === null ? next.delete(key) : next.set(key, value),
    )
    latestParams.current = next
    setSearchParams(next, { replace: true })
  }

  const setSearch = (value: string) => {
    const next = toSearch(value)
    if (next === toSearch(latestParams.current.get(searchKey))) return
    write({ [searchKey]: next || null, [pageKey]: null })
  }

  // Any other sort starts the cycle over.
  const nextNameSort = () => {
    const index = NAME_SORT_CYCLE.findIndex(
      ([key, dir]) => key === sortBy && dir === sortDir,
    )
    return NAME_SORT_CYCLE[(index + 1) % NAME_SORT_CYCLE.length]
  }

  // The first click on a column sorts highest first; further clicks toggle.
  // A name key steps through the name sort instead.
  const toggleSort = (key: SortKey) => {
    const [nextKey, dir] = isNameSortKey(key)
      ? nextNameSort()
      : [key, key === sortBy && sortDir === "desc" ? "asc" : "desc"]
    write({
      [sortParamKey]: nextKey === DEFAULT_SORT ? null : nextKey,
      [dirKey]: dir === DEFAULT_DIR ? null : dir,
      [pageKey]: null,
    })
  }

  // The name column sorts by the active name key, or starts at the first name.
  const nameSortKey: NameSortKey = isNameSortKey(sortBy) ? sortBy : "first_name"
  const nameSortSuffix = isNameSortKey(sortBy)
    ? ` (${t(sortBy === "first_name" ? "students.firstName" : "students.lastName")})`
    : ""

  const setPage = (next: number) =>
    write({ [pageKey]: next > 1 ? String(next) : null })

  return {
    search,
    sortBy,
    sortDir,
    page,
    setSearch,
    toggleSort,
    nameSortKey,
    nameSortSuffix,
    setPage,
    write,
  }
}

export default useDashboardTableState
